import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Building2, Loader2, GraduationCap, Receipt, Users, FileText, CheckCircle2, XCircle, CalendarDays, Award } from "lucide-react";
import { authHeaders, BESTELL_STATUS, fmtDate, fmtEur, formatApiErrorDetail } from "@/components/schulungen/roles";
import { StatusBadge } from "@/components/schulungen/FirmenBestellungen";
import { TermineTab, FreigabenTab, TeilnehmerTab } from "@/components/crm/CRMSchulungenTabs";
import { ResetLinkButton } from "@/components/schulungen/ResetLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Empty({ icon: Icon, text, testId }) {
  return <div className="p-12 text-center text-slate-500" data-testid={testId}><Icon className="h-10 w-10 mx-auto mb-3 text-slate-300" />{text}</div>;
}

function FirmenTab({ firmen, reload }) {
  if (firmen.length === 0) return <Empty icon={Building2} text="Noch keine Firma registriert." testId="firmen-empty" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="firmen-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="px-5 py-3 font-semibold">Firma</th><th className="px-5 py-3 font-semibold">Ansprechpartner</th><th className="px-5 py-3 font-semibold">Kontakt</th><th className="px-5 py-3 font-semibold">Ort</th><th className="px-5 py-3 font-semibold text-right">Plätze frei / gesamt</th><th className="px-5 py-3 font-semibold">Registriert</th><th className="px-5 py-3 font-semibold">Zugang</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {firmen.map((f) => (
            <tr key={f.id} className="hover:bg-slate-50/60" data-testid={`firma-row-${f.id}`}>
              <td className="px-5 py-3 font-semibold text-slate-800">{f.firmenname}</td>
              <td className="px-5 py-3 text-slate-700">{f.ansprechpartner}</td>
              <td className="px-5 py-3 text-slate-600"><div>{f.email}</div>{f.telefon && <div className="text-xs text-slate-400">{f.telefon}</div>}</td>
              <td className="px-5 py-3 text-slate-600">{f.plz} {f.ort}</td>
              <td className="px-5 py-3 text-right font-medium text-slate-800" data-testid={`firma-plaetze-${f.id}`}>{f.plaetze_frei ?? 0} / {f.plaetze_gesamt ?? 0}</td>
              <td className="px-5 py-3 text-slate-500">{fmtDate(f.created_at)}</td>
              <td className="px-5 py-3"><ResetLinkButton endpoint={`/schulungen/firmen/${f.id}/reset-link`} angefordert={!!f.reset_angefordert_am} testId={`firma-reset-btn-${f.id}`} onDone={reload} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RechnungDialog({ bestellung, onClose, onSaved }) {
  const { user } = useAuth();
  const [nr, setNr] = useState("");
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(`${API}/schulungen/bestellungen/${bestellung.id}/rechnung`, { rechnungsnummer: nr, rechnungsdatum: datum }, authHeaders(user));
      toast.success("Rechnung erfasst");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <Dialog open={!!bestellung} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="rechnung-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Rechnung erfassen</DialogTitle>
          <DialogDescription>{bestellung?.firmenname} · {bestellung?.anzahl} Plätze · {fmtEur(bestellung?.gesamt)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Rechnungsnummer</Label><Input required value={nr} onChange={(e) => setNr(e.target.value)} className="h-12 rounded-xl" placeholder="z. B. RE-2026-001" data-testid="rechnung-nr" /></div>
          <div><Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Rechnungsdatum</Label><Input type="date" required value={datum} onChange={(e) => setDatum(e.target.value)} className="h-12 rounded-xl" data-testid="rechnung-datum" /></div>
          <Button type="submit" disabled={loading} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 font-semibold" data-testid="rechnung-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Speichern – Status: Rechnung gestellt"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BestellungenTab({ bestellungen, reload }) {
  const { user } = useAuth();
  const [rechnungFor, setRechnungFor] = useState(null);
  const [filter, setFilter] = useState("alle");

  const bezahlt = async (b) => {
    if (!window.confirm(`Zahlungseingang für ${b.firmenname} (${fmtEur(b.gesamt)}) bestätigen? Danach können Zertifikate für ${b.anzahl} Plätze ausgestellt werden.`)) return;
    try { await axios.patch(`${API}/schulungen/bestellungen/${b.id}/bezahlt`, {}, authHeaders(user)); toast.success(`Zahlung für ${b.firmenname} erfasst – Firma wurde per E-Mail informiert`); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const storno = async (b) => {
    if (!window.confirm(`Bestellung von ${b.firmenname} stornieren?`)) return;
    try { await axios.patch(`${API}/schulungen/bestellungen/${b.id}/storno`, {}, authHeaders(user)); toast.success("Bestellung storniert"); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const list = filter === "alle" ? bestellungen : bestellungen.filter((b) => b.status === filter);
  const offen = bestellungen.filter((b) => b.status === "offen").length;

  return (
    <div>
      <div className="p-4 border-b border-slate-100 flex flex-wrap gap-2 items-center">
        {[["alle", "Alle"], ["offen", `Offen${offen ? ` (${offen})` : ""}`], ["rechnung_gestellt", "Rechnung gestellt"], ["bezahlt", "Bezahlt"], ["storniert", "Storniert"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`h-9 px-3 rounded-lg text-xs font-semibold border transition-colors ${filter === k ? "bg-cyan-700 text-white border-cyan-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid={`filter-${k}`}>{l}</button>
        ))}
      </div>
      {list.length === 0 ? <Empty icon={Receipt} text="Keine Bestellungen in dieser Ansicht." testId="bestellungen-empty" /> : (
        <div className="divide-y divide-slate-100">
          {list.map((b) => (
            <div key={b.id} className="p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`crm-bestellung-${b.id}`}>
              <div className="flex-1 min-w-[220px]">
                <p className="font-semibold text-slate-800">{b.firmenname} · {b.anzahl} Plätze · {fmtEur(b.gesamt)}</p>
                <p className="text-xs text-slate-500">Bestellt {fmtDate(b.created_at)}{b.rechnungsnummer && ` · Rechnung ${b.rechnungsnummer} (${fmtDate(b.rechnungsdatum)})`}{b.bezahlt_am && ` · bezahlt ${fmtDate(b.bezahlt_am)}`}</p>
                {b.bemerkung && <p className="text-xs text-slate-400 mt-0.5">„{b.bemerkung}"</p>}
              </div>
              <StatusBadge map={BESTELL_STATUS} status={b.status} />
              {(b.status === "offen" || b.status === "rechnung_gestellt") && (
                <div className="flex flex-wrap gap-2">
                  {b.status === "offen" && <Button size="sm" variant="outline" className="rounded-lg h-9" onClick={() => setRechnungFor(b)} data-testid={`rechnung-btn-${b.id}`}><FileText className="h-4 w-4 mr-1.5" />Rechnung erfassen</Button>}
                  <Button size="sm" className="rounded-lg h-9 bg-green-600 hover:bg-green-700 text-white" onClick={() => bezahlt(b)} data-testid={`bezahlt-btn-${b.id}`}><CheckCircle2 className="h-4 w-4 mr-1.5" />Zahlung eingegangen</Button>
                  <button onClick={() => storno(b)} className="inline-flex items-center gap-1 h-9 px-2 text-xs text-slate-400 hover:text-red-600" data-testid={`crm-storno-${b.id}`}><XCircle className="h-4 w-4" />Storno</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <RechnungDialog bestellung={rechnungFor} onClose={() => setRechnungFor(null)} onSaved={reload} />
    </div>
  );
}

export default function CRMSchulungen() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/schulungen/firmen`, authHeaders(user)),
      axios.get(`${API}/schulungen/bestellungen`, authHeaders(user)),
      axios.get(`${API}/schulungen/teilnehmer`, authHeaders(user)),
      axios.get(`${API}/schulungen/termine`, authHeaders(user)),
      axios.get(`${API}/schulungen/freigaben`, authHeaders(user)),
    ]).then(([f, b, t, te, fr]) => setData({ firmen: f.data, bestellungen: b.data, teilnehmer: t.data, termine: te.data, freigaben: fr.data }))
      .catch(() => toast.error("Schulungsdaten konnten nicht geladen werden"));
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const offen = data?.bestellungen.filter((b) => b.status !== "bezahlt" && b.status !== "storniert").length || 0;

  return (
    <div className="space-y-6" data-testid="crm-schulungen">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center"><GraduationCap className="h-6 w-6" /></div>
        <div><h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">Schulungen</h1><p className="text-sm text-slate-500">Firmen, Bestellungen & Teilnehmende der B2B-Schulungsplattform</p></div>
      </div>

      {!data ? <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-700" /></div> : (
        <Tabs defaultValue={data.freigaben.length > 0 ? "freigaben" : offen > 0 ? "bestellungen" : "firmen"}>
          <TabsList className="bg-white border border-slate-200/60 rounded-xl h-auto p-1 flex-wrap justify-start">
            <TabsTrigger value="firmen" className="rounded-lg px-4 py-2.5 data-[state=active]:bg-cyan-700 data-[state=active]:text-white" data-testid="crm-tab-firmen"><Building2 className="h-4 w-4 mr-2" />Firmen ({data.firmen.length})</TabsTrigger>
            <TabsTrigger value="bestellungen" className="rounded-lg px-4 py-2.5 data-[state=active]:bg-cyan-700 data-[state=active]:text-white" data-testid="crm-tab-bestellungen"><Receipt className="h-4 w-4 mr-2" />Bestellungen{offen > 0 && <span className="ml-2 h-5 min-w-5 px-1.5 rounded-full bg-amber-400 text-slate-900 text-xs font-bold inline-flex items-center justify-center" data-testid="offene-bestellungen-badge">{offen}</span>}</TabsTrigger>
            <TabsTrigger value="teilnehmer" className="rounded-lg px-4 py-2.5 data-[state=active]:bg-cyan-700 data-[state=active]:text-white" data-testid="crm-tab-teilnehmer"><Users className="h-4 w-4 mr-2" />Teilnehmende ({data.teilnehmer.length})</TabsTrigger>
            <TabsTrigger value="termine" className="rounded-lg px-4 py-2.5 data-[state=active]:bg-cyan-700 data-[state=active]:text-white" data-testid="crm-tab-termine"><CalendarDays className="h-4 w-4 mr-2" />Termine</TabsTrigger>
            <TabsTrigger value="freigaben" className="rounded-lg px-4 py-2.5 data-[state=active]:bg-cyan-700 data-[state=active]:text-white" data-testid="crm-tab-freigaben"><Award className="h-4 w-4 mr-2" />Freigaben{data.freigaben.length > 0 && <span className="ml-2 h-5 min-w-5 px-1.5 rounded-full bg-green-500 text-white text-xs font-bold inline-flex items-center justify-center" data-testid="freigaben-badge">{data.freigaben.length}</span>}</TabsTrigger>
          </TabsList>
          <div className="mt-4 bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
            <TabsContent value="firmen" className="m-0"><FirmenTab firmen={data.firmen} reload={load} /></TabsContent>
            <TabsContent value="bestellungen" className="m-0"><BestellungenTab bestellungen={data.bestellungen} reload={load} /></TabsContent>
            <TabsContent value="teilnehmer" className="m-0"><TeilnehmerTab teilnehmer={data.teilnehmer} reload={load} /></TabsContent>
            <TabsContent value="termine" className="m-0"><TermineTab termine={data.termine} reload={load} /></TabsContent>
            <TabsContent value="freigaben" className="m-0"><FreigabenTab freigaben={data.freigaben} reload={load} /></TabsContent>
          </div>
        </Tabs>
      )}
    </div>
  );
}
